import { type ReactElement, useCallback } from 'react';
import { useRefreshTime } from './use-refresh';
import { TransactionStatus } from '@constants/transaction-status';
import { transactionErrorMap } from '@constants/transaction-error-map';
import { Transaction } from '@mysten/sui/transactions';
import { transactionModel } from '@models/transaction.model';
import {
  useCurrentAccount,
  useCurrentWallet,
  useSignAndExecuteTransaction,
  useSignTransaction,
  useSuiClient,
} from '@mysten/dapp-kit';
import { STASHED_WALLET_NAME, ZkSendLinkBuilder } from '@mysten/zksend';

export const useTransaction = () => {
  const client = useSuiClient();
  const { mutateAsync: signAndExecuteTransaction } = useSignAndExecuteTransaction();
  const { mutateAsync: signTransaction } = useSignTransaction();

  const currentAccount = useCurrentAccount();
  const { currentWallet } = useCurrentWallet();

  const { updateRefresh } = useRefreshTime();

  const transact = useCallback(
    async (params: {
      title: string | ReactElement;
      description?: string | ReactElement;
      fn: () => Promise<Transaction | undefined>;

      successTitle?: string | ReactElement;
      successDescription?: string | ReactElement;

      errorTitle?: string | ReactElement;
      errorDescription?: string | ReactElement;
    }): Promise<{ status: boolean; digest?: string }> => {
      const { fn, ...option } = params;

      transactionModel.toggleVisible();
      transactionModel.update({
        status: TransactionStatus.confirm,
        ...option,
      });

      try {
        const txb = await fn();
        if (!txb) {
          transactionModel.update({
            status: undefined,
          });
          return {
            status: false,
          };
        }

        if (currentWallet?.name === STASHED_WALLET_NAME && currentAccount) {
          const link = new ZkSendLinkBuilder({
            sender: currentAccount.address,
            client,
          });
          link.createSendTransaction({
            transaction: txb,
          });
        }
        const { signature, bytes } = await signTransaction({ transaction: txb });

        // const now = Date.now();
        const { digest } = await client.executeTransactionBlock({
          signature: signature,
          transactionBlock: bytes,
        });

        // const end = Date.now() - now;
        const response = await client.waitForTransaction({
          digest,
          options: {
            showEffects: true,
          },
        });
        const effects = response.effects;
        if (effects?.status.status === 'success') {
          transactionModel.update({
            status: TransactionStatus.success,
            digest,
          });
          updateRefresh(300);
          return {
            status: true,
            digest,
          };
        } else {
          throw new Error(effects?.status.error || '');
        }
      } catch (e) {
        console.error('Transaction Exception:\n\n', (e as Error).stack || (e as Error).message);
        const errorData = transformMessage((e as Error).message);
        const errorMsg = typeof errorData === 'string' ? errorData : errorData.description;

        transactionModel.update({
          status: TransactionStatus.fail,
          errorDescription: errorMsg,
        });
        return {
          status: false,
        };
      }
    },
    [
      updateRefresh,
      signAndExecuteTransaction,
      signTransaction,
      client,
      currentAccount,
      currentWallet,
    ],
  );

  return transact;
};

const isRejectedFromUser = (msg: string) => {
  return msg === 'Rejected from user' || msg.indexOf('user rejection') >= 0;
};

const transformMessage = (errorMessage: string) => {
  if (isRejectedFromUser(errorMessage)) return errorMessage;

  const matches = errorMessage.match(/name: Identifier\("(\w+)"\).*},\s*(\d+)\)/) as
    | [string, string, string]
    | null;
  if (!matches) return 'Transaction was not processed. Please try again.';

  const [_, swapRouter, value] = matches;
  const mappedMsg = transactionErrorMap[swapRouter]?.[value];
  if (mappedMsg) return mappedMsg;

  return `${
    swapRouter.toLowerCase().indexOf('swap') >= 0 ? 'Swap' : 'Transaction'
  } was not processed. Please try again.`;
};
